'use client';

import { c as _c } from "react/compiler-runtime";
import AccordionItem from '@/features/payload-cms/components/accordion/accordion-item';
import { AccordionSearchBar } from '@/features/payload-cms/components/accordion/accordion-search-bar';
import { TeamLeaderPortrait } from '@/features/payload-cms/components/accordion/team-members/team-leader-portrait';
import type { AccordionBlocks, Image } from '@/features/payload-cms/payload-types';
import type { Locale, StaticTranslationString } from '@/types/types';
import { replaceUmlautsAndAccents } from '@/utils/node-to-anchor-reference';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
const searchPlaceholder: StaticTranslationString = {
  de: 'Suche…',
  en: 'Search…',
  fr: 'Rechercher…'
};
const noResultsLabel: StaticTranslationString = {
  de: 'Keine Einträge gefunden.',
  en: 'No entries found.',
  fr: 'Aucun résultat trouvé.'
};
const clearSearchLabel: StaticTranslationString = {
  de: 'Suche löschen',
  en: 'Clear search',
  fr: 'Effacer la recherche'
};

/**
 * Sanitizes a title string into a URL-friendly fragment.
 */
const sanitizeTitle = (title: string): string => {
  return replaceUmlautsAndAccents(title).replaceAll(/\W+/g, '-');
};

/**
 * Gets the unique fragment identifier for an accordion block.
 */
const getFragmentFromBlock = (accordionBlock: NonNullable<AccordionBlocks['accordionBlocks']>[number]): string => {
  const titleOrPortrait = accordionBlock.titleOrPortrait;
  if (titleOrPortrait === 'portrait') {
    if (accordionBlock.teamLeaderGroup === undefined) {
      return '';
    }
    const teamLeaderGroup = accordionBlock.teamLeaderGroup as {
      name: string;
    };
    return sanitizeTitle(teamLeaderGroup.name);
  }

  // Default to title behavior if 'portrait' is not explicitly selected
  const title = accordionBlock.title as string | undefined ?? '';
  return title === '' ? '' : sanitizeTitle(title);
};

/**
 * Creates the title element (either text or portrait) for the accordion item.
 */
const createTitleElement = (accordionBlock: NonNullable<AccordionBlocks['accordionBlocks']>[number]): React.ReactNode => {
  if (accordionBlock.titleOrPortrait === 'portrait') {
    if (accordionBlock.teamLeaderGroup === undefined) return <></>;
    const teamLeaderGroup = accordionBlock.teamLeaderGroup;
    const name: string = teamLeaderGroup.name;
    const ceviname: string = teamLeaderGroup.ceviname ?? '';
    const portrait: string | Image | null | undefined = teamLeaderGroup.portrait;
    return <div className="group flex w-full cursor-pointer flex-col items-center gap-4 rounded-md px-2 py-4 text-center transition-colors md:flex-row md:py-2 md:text-left">
        <div className="relative h-48 w-48 shrink-0 overflow-hidden rounded-full md:h-24 md:w-24">
          {<TeamLeaderPortrait name={name} portrait={portrait} hoverEffect={false} />}
        </div>
        <div className="w-full">
          <p className="font-medium text-gray-900">{name}</p>
          {ceviname !== '' && <p className="text-sm text-gray-500">{ceviname}</p>}
        </div>
      </div>;
  }

  // Default to rendering title if 'portrait' is not selected or if title exists
  return <h3 className="text-lg font-medium text-gray-900">{accordionBlock.title}</h3>;
};
const AccordionClientContainer: React.FC<{
  accordionBlocks: AccordionBlocks['accordionBlocks'];
  childs: {
    [key: string]: React.ReactNode;
  };
  isNested?: boolean;
  enableSearch?: boolean;
  searchIndex?: Record<string, string>;
  locale?: Locale;
}> = t0 => {
  const $ = _c(46);
  const {
    accordionBlocks,
    childs,
    isNested,
    enableSearch,
    searchIndex,
    locale
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [expandedIds, setExpandedIds] = useState(t1);
  const [rawQuery, setRawQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {};
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const accordionItemReferences = useRef(t2);
  const lastHandledHashReference = useRef(undefined);
  let t3;
  let t4;
  if ($[2] !== rawQuery) {
    t3 = () => {
      const timer = setTimeout(() => {
        setDebouncedQuery(rawQuery.trim().toLowerCase());
      }, 150);
      return () => clearTimeout(timer);
    };
    t4 = [rawQuery];
    $[2] = rawQuery;
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useEffect(t3, t4);
  let t5;
  bb0: {
    if (!enableSearch || debouncedQuery === "" || searchIndex === undefined) {
      let t6;
      if ($[5] !== accordionBlocks) {
        t6 = accordionBlocks ?? [];
        $[5] = accordionBlocks;
        $[6] = t6;
      } else {
        t6 = $[6];
      }
      t5 = t6;
      break bb0;
    }
    let t6;
    if ($[7] !== accordionBlocks) {
      t6 = accordionBlocks ?? [];
      $[7] = accordionBlocks;
      $[8] = t6;
    } else {
      t6 = $[8];
    }
    let t7;
    if ($[9] !== debouncedQuery || $[10] !== searchIndex || $[11] !== t6) {
      let t8;
      if ($[13] !== debouncedQuery || $[14] !== searchIndex) {
        t8 = block => {
          const indexed = block.id !== undefined && block.id !== null ? searchIndex[block.id] ?? "" : "";
          return indexed.includes(debouncedQuery);
        };
        $[13] = debouncedQuery;
        $[14] = searchIndex;
        $[15] = t8;
      } else {
        t8 = $[15];
      }
      t7 = t6.filter(t8);
      $[9] = debouncedQuery;
      $[10] = searchIndex;
      $[11] = t6;
      $[12] = t7;
    } else {
      t7 = $[12];
    }
    t5 = t7;
  }
  const visibleBlocks = t5;
  const updateURLFragment = _temp;
  let t6;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = fragment_0 => {
      const element = accordionItemReferences.current[fragment_0];
      if (element) {
        element.scrollIntoView({
          behavior: "smooth",
          block: "start"
        });
      }
    };
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const scrollToElement = t6;
  let t7;
  if ($[17] !== expandedIds) {
    t7 = accordionBlock => {
      const fragment_1 = getFragmentFromBlock(accordionBlock);
      const isClosing = expandedIds.includes(fragment_1);
      setExpandedIds(previousExpandedIds => {
        if (previousExpandedIds.includes(fragment_1)) {
          return previousExpandedIds.filter(id => id !== fragment_1);
        } else {
          return [...previousExpandedIds, fragment_1];
        }
      });
      updateURLFragment(isClosing ? undefined : fragment_1);
    };
    $[17] = expandedIds;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const toggleExpand = t7;
  let t8;
  let t9;
  if ($[19] !== accordionBlocks || $[20] !== isNested) {
    t8 = () => {
      if (isNested === true) {
        return;
      }
      if (typeof globalThis !== "undefined" && globalThis.location) {
        const hash = globalThis.location.hash.slice(1);
        if (hash === "") {
          lastHandledHashReference.current = undefined;
          return;
        }
        if (lastHandledHashReference.current === hash) {
          return;
        }
        const isValidFragment = accordionBlocks?.some(block_0 => getFragmentFromBlock(block_0) === hash);
        if (isValidFragment === true) {
          lastHandledHashReference.current = hash;
          const timer1 = setTimeout(() => {
            setExpandedIds([hash]);
          }, 0);
          const timer2 = setTimeout(() => {
            scrollToElement(hash);
          }, 150);
          return () => {
            clearTimeout(timer1);
            clearTimeout(timer2);
          };
        }
      }
    };
    t9 = [accordionBlocks, scrollToElement, isNested];
    $[19] = accordionBlocks;
    $[20] = isNested;
    $[21] = t8;
    $[22] = t9;
  } else {
    t8 = $[21];
    t9 = $[22];
  }
  useEffect(t8, t9);
  const totalCount = accordionBlocks?.length ?? 0;
  const effectiveLocale = locale ?? "de";
  let t10;
  if ($[23] !== effectiveLocale || $[24] !== enableSearch || $[25] !== isNested || $[26] !== rawQuery || $[27] !== totalCount || $[28] !== visibleBlocks.length) {
    t10 = enableSearch === true && isNested !== true && <AccordionSearchBar query={rawQuery} onChange={setRawQuery} placeholder={searchPlaceholder[effectiveLocale]} noResultsLabel={noResultsLabel[effectiveLocale]} clearSearchLabel={clearSearchLabel[effectiveLocale]} resultCount={visibleBlocks.length} totalCount={totalCount} />;
    $[23] = effectiveLocale;
    $[24] = enableSearch;
    $[25] = isNested;
    $[26] = rawQuery;
    $[27] = totalCount;
    $[28] = visibleBlocks.length;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== childs || $[31] !== expandedIds || $[32] !== isNested || $[33] !== toggleExpand || $[34] !== visibleBlocks) {
    let t12;
    if ($[36] !== childs || $[37] !== expandedIds || $[38] !== isNested || $[39] !== toggleExpand) {
      t12 = accordionBlock_0 => {
        const fragment_2 = getFragmentFromBlock(accordionBlock_0);
        return <div key={accordionBlock_0.id ?? fragment_2} ref={element_0 => {
          accordionItemReferences.current[fragment_2] = element_0;
        }} className="scroll-mt-10">{accordionBlock_0.id !== undefined && accordionBlock_0.id !== null && <AccordionItem titleElement={createTitleElement(accordionBlock_0)} showChevron={accordionBlock_0.titleOrPortrait !== "portrait"} accordionId={accordionBlock_0.id} isExpanded={expandedIds.includes(fragment_2)} onToggle={() => toggleExpand(accordionBlock_0)} isNested={isNested ?? false}>{expandedIds.includes(fragment_2) && childs[accordionBlock_0.id ?? ""]}</AccordionItem>}</div>;
      };
      $[36] = childs;
      $[37] = expandedIds;
      $[38] = isNested;
      $[39] = toggleExpand;
      $[40] = t12;
    } else {
      t12 = $[40];
    }
    t11 = visibleBlocks.map(t12);
    $[30] = childs;
    $[31] = expandedIds;
    $[32] = isNested;
    $[33] = toggleExpand;
    $[34] = visibleBlocks;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  let t12;
  if ($[41] !== t11) {
    t12 = <div className="space-y-4">{t11}</div>;
    $[41] = t11;
    $[42] = t12;
  } else {
    t12 = $[42];
  }
  let t13;
  if ($[43] !== t10 || $[44] !== t12) {
    t13 = <div>{t10}{t12}</div>;
    $[43] = t10;
    $[44] = t12;
    $[45] = t13;
  } else {
    t13 = $[45];
  }
  return t13;
};
export default AccordionClientContainer;
function _temp(fragment) {
  if (typeof globalThis !== "undefined" && globalThis.location) {
    if (fragment === undefined) {
      const newUrl = globalThis.location.pathname + globalThis.location.search;
      globalThis.history.replaceState(undefined, "", newUrl);
    } else {
      globalThis.history.replaceState(undefined, "", `#${fragment}`);
    }
  }
}